import { Cli } from "incur";
import { nativeSpec, filterSpecByTag, createWhopFetch } from "./api";
import { API_GROUPS, registerHandwrittenGroups } from "./commands";
import { buildMediaGroup } from "./media/commands";
import pkg from "../package.json" with { type: "json" };
import { render } from "./lib/renderer";
import { performLogin } from "./auth/commands";
import { getActiveProfile } from "./auth/profiles";
import { PromptCancelledError } from "./auth/prompts";

const cli = Cli.create("whop", {
	version: pkg.version,
	description: "Build and manage Whop apps from your terminal.",
	renderer: render,
	sync: {
		// Shipped skill files generated by scripts/sync-spec.ts from the
		// spec's own tag copy (x-whop-summary + description, authored in
		// backend/openapi/v1/tags.yml). A matching directory name overrides
		// the auto-generated skill for that command group.
		include: ["skills/*"],
		suggestions: [
			"choose or create my Whop business account",
			"show my Whop business accounts",
			"create a Whop product and pricing plan",
		],
	},
	mcp: {
		agents: ["claude-desktop"],
	},
});

const spec = (tag: string) =>
	filterSpecByTag(nativeSpec as Record<string, unknown>, tag);
const fetch = createWhopFetch();

const isAuthExempt = (cmd: string) =>
	!cmd ||
	cmd === "login" ||
	cmd === "logout" ||
	cmd === "completions" ||
	cmd === "upgrade" ||
	cmd.startsWith("auth") ||
	cmd.startsWith("mcp");

cli.use(async (c, next) => {
	if (isAuthExempt(c.command)) return next();
	if (getActiveProfile()) return next();
	if (process.env.WHOP_API_KEY) return next();

	if (c.agent || c.formatExplicit) {
		return c.error({
			code: "NOT_AUTHENTICATED",
			message: "Not logged in. Set WHOP_API_KEY or run `whop login` first.",
			cta: {
				commands: [{ command: "auth login", description: "Log in to Whop" }],
			},
		});
	}

	let result;
	try {
		result = await performLogin({
			agent: false,
			deferAccountSelection: c.command === "quickstart",
		});
	} catch (err) {
		if (err instanceof PromptCancelledError) {
			return c.error({
				code: "CANCELLED",
				message: "Login cancelled.",
				exitCode: 130,
			});
		}
		throw err;
	}

	if (!result.ok) {
		return c.error({
			code: result.code,
			message: result.message,
			cta: {
				commands: [{ command: "auth login", description: "Log in to Whop" }],
			},
		});
	}

	return next();
});

await registerHandwrittenGroups(cli);

for (const { name, tag, description } of API_GROUPS) {
	// Media is hybrid: spec commands plus a hand-written `generate --wait`.
	// Registered here (not HANDWRITTEN_GROUPS) so it keeps its taxonomy slot in help.
	if (name === "media") {
		cli.command(await buildMediaGroup(description));
		continue;
	}
	cli.command(name, { description, fetch, openapi: spec(tag) });
}

export default cli;
